import { serverUri } from 'src/config'
import Generic from 'src/models/generic/Generic'
import Model, { ID, IsoDateTime, JsonArray, JsonObject } from 'src/models/model'

export const DATA_MIGRATION_TYPES = Object.freeze({
  USER: 'user',
  USER_POINT: 'user_point',
  USER_VOUCHER: 'user_voucher',
  VOUCHER: 'voucher',
  GIFT_CARD: 'gift_card',
  CARD: 'card',
  TICKET: 'ticket',
  ITEM_VARIATION: 'item_variation',
  INVENTORY_RECIPE_ITEM: 'inventory_recipe_item',
  ORDER: 'order'
})

export const VALIDATION_STATES = Object.freeze({
  WAITING: 'waiting',
  MAPPING_REQUIRED: 'mapping-required',
  VALIDATING: 'validating',
  VALIDATING_WITH_ERRORS: 'validating-with-errors',
  VALIDATED_WITH_ERRORS: 'validated-with-errors',
  VALIDATED: 'validated'
})

export const IMPORT_STATES = Object.freeze({
  WAITING: 'waiting',
  FAILED_TO_START: 'failed-to-start',
  IMPORTING: 'importing',
  IMPORTING_WITH_ERRORS: 'importing-with-errors',
  IMPORTED_WITH_ERRORS: 'imported-with-errors',
  IMPORTED: 'imported'
})

export default class CinemaDataMigration extends Model {
  static name = 'CinemaDataMigration'

  static paginate = true

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Data]

  static define () {
    this.fields = {
      id: {
        type: ID,
        required: true
      },
      dataType: {
        type: String,
        options: Object.values(DATA_MIGRATION_TYPES),
        required: true
      },
      dataUrl: {
        type: String,
        input: true
      },
      dataUrlDownloadLink: {
        type: String,
        output: true
      },
      validationState: {
        type: String,
        output: true,
        default: null
      },
      importState: {
        type: String,
        output: true,
        default: null
      },
      headersMapping: {
        type: JsonObject,
        default () {
          return {
          }
        }
      },
      headerValidationMessage: {
        type: JsonArray,
        output: true
      },
      headersValidationSuccess: {
        type: Boolean,
        output: true
      },
      allValidColumns: {
        type: JsonArray,
        output: true
      },
      requiredColumns: {
        type: JsonArray,
        output: true
      },
      importStarted: {
        type: Boolean,
        output: true,
        default: false
      },
      importConfiguration: {
        type: JsonObject,
        default () {
          return {}
        }
      },
      updatedAt: {
        type: IsoDateTime,
        output: true
      },
      action: {
        type: String,
        default: null,
        input: true
      }
    }

    this.queries = {
      importConfigurationOptions: {
        results: {
          summary: Generic
        }
      }
    }

    this.computed = {
      validationStatus () {
        if (this.validationState === VALIDATION_STATES.VALIDATED) {
          return { name: 'check', color: 'positive', tooltip: 'Validation Successful' }
        } else if (this.validationState === VALIDATION_STATES.MAPPING_REQUIRED) {
          return { name: 'warning', color: 'warning', tooltip: 'Mapping Required' }
        } else if (this.validationState === VALIDATION_STATES.VALIDATED_WITH_ERRORS || this.validationState === VALIDATION_STATES.VALIDATING_WITH_ERRORS) {
          return { name: 'error', color: 'negative', tooltip: 'Some Validations Have Failed' }
        } else if (this.validationState === VALIDATION_STATES.VALIDATING) {
          return { name: 'manage_search', color: 'warning', tooltip: 'Validation in Progress' }
        } else {
          return { name: 'hourglass_top', color: 'warning', tooltip: 'Queued, Waiting to be Processed' }
        }
      },
      importStatus () {
        if (this.importState === IMPORT_STATES.IMPORTED) {
          return { name: 'check', color: 'positive', tooltip: 'Import Completed' }
        } else if (this.importState === IMPORT_STATES.IMPORTED_WITH_ERRORS) {
          return { name: 'error', color: 'negative', tooltip: 'Import Completed, With Errors' }
        } else if (this.importState === IMPORT_STATES.IMPORTING_WITH_ERRORS) {
          return { name: 'manage_search', color: 'negative', tooltip: 'Import Completed, With Errors' }
        } else if (this.importState === IMPORT_STATES.IMPORTING) {
          return { name: 'manage_search', color: 'warning', tooltip: 'Import in Progress' }
        } else if (this.importState === IMPORT_STATES.FAILED_TO_START) {
          return { name: 'close', color: 'negative', tooltip: 'All Records Failed to Validate, No Import Was Attempted' }
        } else {
          return { name: 'hourglass_top', color: 'warning', tooltip: 'Queued, Waiting to be Processed' }
        }
      },
      downloadLink () {
        return { name: 'Download CSV', link: serverUri + this.dataUrlDownloadLink }
      },
      editable () {
        if (this.importState === IMPORT_STATES.WAITING) return true

        if (this.isLocal) return true

        return false
      }
    }
  }
}
